import { inject, Injectable } from "@angular/core";
import { UserDto, UserDataDto } from "@clients/firestore/dtos/users.dto";
import { FirestoreClient, UserDataCollectionId, UsersCollectionId } from "@clients/firestore/firestore.client";
import { EDUCATION_LEVEL_GROUPS } from "@config";
import { DifficultyLevelRange } from "@domain/AdaptiveLearning";
import { RewardCursors } from "@domain/RewardCursors";
import { User, UserSchoolGrade } from "@domain/User";

const USERS_COLLECTION_ID: UsersCollectionId = "users";
const USER_DATA_COLLECTION_ID: UserDataCollectionId = "user_data";

@Injectable({
  providedIn: "root",
})
export class UserClient {
  private firestoreClient = inject(FirestoreClient);

  async getUserById(userFirebaseUid: string): Promise<User> {
    const userDto = await this.firestoreClient.read<UserDto>(USERS_COLLECTION_ID, userFirebaseUid);

    if (!userDto) {
      throw new Error("user not found");
    }

    const userDataDto = await this.firestoreClient.read<UserDataDto>(USER_DATA_COLLECTION_ID, userFirebaseUid);

    return this.mapToDomain(userFirebaseUid, userDto, userDataDto);
  }

  async updateDifficultyLevel(user: User, newDifficultyLevel: DifficultyLevelRange): Promise<void> {
    this.firestoreClient.create({ difficultyLevel: newDifficultyLevel }, USER_DATA_COLLECTION_ID, user.id);
  }

  async setRewardMechanismAcknoledgement(user: User) {
    this.firestoreClient.create({ hasAcknowledgedRewardCursor: user.hasAcknowledgedRewardCursor }, USER_DATA_COLLECTION_ID, user.id);
  }

  async updateUser(user: User) {
    const userDataDto = this.mapToUserDataDto(user);
    this.firestoreClient.create(userDataDto, USER_DATA_COLLECTION_ID, user.id);
  }

  private mapToDomain(userFirebaseUid: string, userDto: UserDto, userDataDto?: UserDataDto): User {
    const hasAcknowledgedRewardCursorDefaultValue = false;
    const showRewardMechanismDefaultValue = true;

    const user = {
      id: userFirebaseUid,
      email: userDto.email,
      username: userDto.nickname,
      schoolGrade: userDto.level,
      educationLevelGroup: this.mapUserSchoolGradeToEducationLevelGroup(userDto.level),
    };

    const rewardCursors = new RewardCursors({
      lastAvailableCursor: userDataDto?.lastAvailableCursor ?? undefined,
      currentCursor: userDataDto?.currentCursor ?? undefined,
    });

    if (!userDataDto)
      return {
        ...user,
        rewardCursors,
        hasAcknowledgedRewardCursor: hasAcknowledgedRewardCursorDefaultValue,
        showRewardMechanism: showRewardMechanismDefaultValue,
        nextRewardCursorCorrectAnswerCount: 0,
      };

    return {
      ...user,
      rewardCursors,
      hasAcknowledgedRewardCursor: userDataDto.hasAcknowledgedRewardCursor ?? hasAcknowledgedRewardCursorDefaultValue,
      showRewardMechanism: userDataDto.showRewardMechanism ?? showRewardMechanismDefaultValue,
      nextRewardCursorCorrectAnswerCount: userDataDto.nextRewardCursorCorrectAnswerCount ?? 0,
    };
  }

  private mapToUserDataDto(user: User): UserDataDto {
    return {
      currentCursor: user.rewardCursors.getCurrentCursor() ?? null,
      lastAvailableCursor: user.rewardCursors.getLastAvailableCursor() ?? null,
      hasAcknowledgedRewardCursor: user.hasAcknowledgedRewardCursor,
      showRewardMechanism: user.showRewardMechanism,
      nextRewardCursorCorrectAnswerCount: user.nextRewardCursorCorrectAnswerCount ?? 0,
    };
  }

  private mapUserSchoolGradeToEducationLevelGroup(userSchoolGrade: UserSchoolGrade) {
    return EDUCATION_LEVEL_GROUPS.find(educationLevelGroup => educationLevelGroup.matchingUserSchoolGrades.includes(userSchoolGrade));
  }
}
